<script lang="ts">
    import { scrollToTop } from "../../../core/helpers/window.helper";
    import { inflect } from "../../../core/helpers/string.helper";
    import { ymdToDmy } from '../../../core/utils/date.util'
    import type ServiceProvider from '../services/service.provider'
    import type DataProvider from '../services/data.provider'
    import type { ISearch } from '../interfaces/search.interface'
    import SearchPager from './Pager.svelte'
    import TypeFilter from "./TypeFilter.svelte";

    export let serviceProvider: ServiceProvider
    export let dataProvider: DataProvider

    let searchList: ISearch[] = []
    let total = 0
    let error = false
    let processing = false

    serviceProvider.searchService.searchListSubs.subscribe((list): void => {
        searchList = list
        scrollToTop()
    })
    serviceProvider.searchService.totalSubs.subscribe((number) => (total = number))
    serviceProvider.searchService.errorSubs.subscribe((status) => (error = status))
    serviceProvider.searchService.processingSubs.subscribe((status) => (processing = status))

    const showAllArticles = (searchItem: ISearch): void => {
        searchItem.isVisibleAllArticles = true
        searchList = searchList
    };
</script>

{#if processing}
    <h2 class="bno-title--alpha is-plain">Vyhledávám...</h2>
{:else if error}
    <h2 class="bno-title--alpha is-plain">Výsledky vyhledávání</h2>
    <p>Nastala chyba při vyhledávání</p>
{:else}
    {#if dataProvider.types && dataProvider.types.length}
        <!-- brno -->
        <h2 class="bno-title--alpha is-plain">Výsledky vyhledávání</h2>
        <TypeFilter {dataProvider} {serviceProvider}></TypeFilter>
    {:else}
        <!-- tis -->
        <h2>{inflect('Nalezeno', total)} {total} {inflect('vysledku', total)}</h2>
    {/if}

    {#if searchList && searchList.length}
        <div class="bno-search-list">
            <ul class="bno-search-list__items">
                {#each searchList as item}
                    <li class="bno-search-list__item bno-search-list__item--variant-b">
                        <h4>
                            {#if item.extension}
                                <span class="bno-icon bno-icon--{item.extension.toLowerCase() === 'pdf' ? 'pdf' : 'doc'}" aria-hidden="true"></span>
                            {/if}
                            <a
                                href={item.url}
                                target="{item.openInNewWindow || item.extension ? '_blank' : ''}"
                                class="{item.extension ? 'bno-link--without-external-icon' : ''}"
                                aria-label={item.title}
                            >
                                {item.title}
                            </a>
                            {#if item.extension}
                                <span>({item.extension}, {item.size})</span>
                            {/if}
                        </h4>

                        {#if item.businessType}
                            <span class="bno-chip bno-chip--arrowed">
                                <span class="bno-chip__inner">
                                    {item.businessType}
                                </span>
                            </span>
                        {:else if item.contentType}
                            <span class="bno-chip bno-chip--arrowed">
                                <span class="bno-chip__inner">
                                    {item.contentType}
                                </span>
                            </span>
                        {/if}

                        {#if ['Článek','Tisková zpráva'].includes(item.contentType) && item.publishDate}
                            <p>{item.publishDate}</p>
                        {/if}

                        {#if item.description}
                            {@html item.description}
                        {/if}

                        {#if item.ucinnostOd || item.ucinnostDo}
                            <p class="bno-search-list__efficiency">
                                <span class:bno-search-list__efficiency--red="{item.platnostOd === false}">
                                    {#if item.platnostOd === false}
                                        <span class="bno-icon bno-icon--warning bno-icon--16x16" style="color: var(--smb-warning-300);" aria-hidden="true"></span>
                                    {/if}
                                    Účinnost od
                                    {#if item.ucinnostOd}
                                        <strong>
                                            {ymdToDmy(item.ucinnostOd)}
                                        </strong>
                                    {:else}
                                        <strong style="color: #878787;">nezadána</strong>
                                    {/if}
                                </span>

                                {#if item.ucinnostDo}
                                    <span class:bno-search-list__efficiency--red="{item.platnostDo === false}">
                                        {#if item.platnostDo === false}
                                            <span class="bno-icon bno-icon--warning bno-icon--16x16" style="color: var(--smb-warning-300);" aria-hidden="true"></span>
                                        {/if}
                                        Účinnost do
                                        <strong>
                                            {ymdToDmy(item.ucinnostDo)}
                                        </strong>
                                    </span>
                                {/if}
                            </p>
                        {/if}

                        {#if item.breadcrumbsList}
                            <div class="bno-search-list__breadcrumb">
                                <nav class="bno-breadcrumbs">
                                    <ol class="bno-breadcrumbs__list">
                                        {#each item.breadcrumbsList as breadcrumbsItem}
                                            <li class="bno-breadcrumbs__item">
                                                <span aria-hidden="true"></span>
                                                <a class="bno-link" href="{breadcrumbsItem.url}" aria-label="{breadcrumbsItem.title} ">{breadcrumbsItem.title}</a>
                                            </li>
                                        {/each}
                                    </ol>
                                </nav>
                            </div>
                        {:else if item.breadcrumbs}
                            {@html item.breadcrumbs}
                        {/if}

                        {#if item.articles && item.articles.length > 0}
                            <div class="bno-search-list__breadcrumb">
                                <nav class="bno-breadcrumbs">
                                    <ol class="bno-breadcrumbs__list">
                                        <li class="bno-breadcrumbs__item">
                                            <a class="bno-link" href="{item.articles[0].url}" aria-label="{item.articles[0].title} ">{item.articles[0].title}</a>
                                        </li>
                                    </ol>
                                </nav>
                                {#if item.articles.length > 1 && !item.isVisibleAllArticles}
                                    <a
                                        class="bno-link"
                                        href="#"
                                        aria-label="Zobrazit vše"
                                        on:click|preventDefault={() => showAllArticles(item)}
                                    >
                                        Zobrazit vše
                                    </a>
                                {/if}
                            </div>
                            {#if item.articles.length > 1 && item.isVisibleAllArticles}
                                {#each item.articles.slice(1) as article}
                                    <div class="bno-search-list__breadcrumb">
                                        <nav class="bno-breadcrumbs">
                                            <ol class="bno-breadcrumbs__list">
                                                <li class="bno-breadcrumbs__item">
                                                    <a class="bno-link" href="{article.url}" aria-label="{article.title} ">{article.title}</a>
                                                </li>
                                            </ol>
                                        </nav>
                                    </div>
                                {/each}
                            {/if}
                        {/if}
                    </li>
                {/each}
            </ul>
        </div>
        {#if dataProvider.types && dataProvider.types.length}
            <!-- brno -->
            <p class="bno-title--gamma">{inflect('Nalezeno', total)} {total} {inflect('vysledku', total)}</p>
        {:else}
            <!-- tis -->
            <!-- nothing -->
        {/if}
        <SearchPager {serviceProvider}/>
    {:else}
        <p>Vašemu zadání neodpovídají žádné výsledky</p>
    {/if}
{/if}
